import { createContext, useContext, useEffect, useState } from 'react';
import { useAuth } from './AuthContext';
import { organizationService } from '../api/services/organization.service';
import { OrganizationDto } from '../api/types/dtos';

interface OrganizationContextType {
  ownOrganizations: OrganizationDto[];
  selectedOrganization: OrganizationDto | null;
  loading: boolean;
  error: Error | null;
  refetchOwnOrganizations: () => Promise<void>;
  changeSelectedOrganization: (orgId: string) => void;
}

const OrganizationContext = createContext<OrganizationContextType>({
  ownOrganizations: [],
  selectedOrganization: null,
  loading: true,
  error: null,
  refetchOwnOrganizations: async () => {},
  changeSelectedOrganization: () => {},
});

export const useOrganization = () => useContext(OrganizationContext);

const OWN_ORGS_KEY = 'papo_own_organizations';
const SELECTED_ORG_KEY = 'papo_selected_organization';

export function OrganizationProvider({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isSessionReady } = useAuth();
  const [ownOrganizations, setOwnOrganizations] = useState<OrganizationDto[]>([]);
  const [selectedOrganization, setSelectedOrganization] = useState<OrganizationDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const saveToStorage = (orgs: OrganizationDto[], selected: OrganizationDto | null) => {
    localStorage.setItem(OWN_ORGS_KEY, JSON.stringify(orgs));
    if (selected) {
      localStorage.setItem(SELECTED_ORG_KEY, JSON.stringify(selected));
    } else {
      localStorage.removeItem(SELECTED_ORG_KEY);
    }
  };

  const fetchOwnOrganizations = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await organizationService.findOwn();
      const orgs = response.data || [];
      
      setOwnOrganizations(orgs);
      localStorage.setItem(OWN_ORGS_KEY, JSON.stringify(orgs));
      
      // Try to restore selected org from storage
      let selected: OrganizationDto | null = null;
      const stored = localStorage.getItem(SELECTED_ORG_KEY);
      
      if (stored) {
        const parsed = JSON.parse(stored);
        selected = orgs.find(o => o.id === parsed.id) || null;
      }
      
      if (!selected && orgs.length > 0) {
        selected = orgs[0];
      }
      
      setSelectedOrganization(selected);
      if (selected) {
        localStorage.setItem(SELECTED_ORG_KEY, JSON.stringify(selected));
      }
    } catch (err) {
      setError(err as Error);
      setOwnOrganizations([]);
      setSelectedOrganization(null);
      localStorage.removeItem(OWN_ORGS_KEY);
      localStorage.removeItem(SELECTED_ORG_KEY);
    } finally {
      setLoading(false);
    }
  };

  const changeSelectedOrganization = (orgId: string) => {
    const org = ownOrganizations.find(o => o.id === orgId) || null;
    setSelectedOrganization(org);
    if (org) {
      localStorage.setItem(SELECTED_ORG_KEY, JSON.stringify(org));
    } else {
      localStorage.removeItem(SELECTED_ORG_KEY);
    }
    // ✅ El AuthorizationContext se actualiza automáticamente cuando cambia selectedOrganization
  };

  useEffect(() => {
    if (isAuthenticated && isSessionReady) {
      fetchOwnOrganizations();
    } else {
      setOwnOrganizations([]);
      setSelectedOrganization(null);
      localStorage.removeItem(OWN_ORGS_KEY);
      localStorage.removeItem(SELECTED_ORG_KEY);
    }
  }, [isAuthenticated, isSessionReady]);

  return (
    <OrganizationContext.Provider
      value={{
        ownOrganizations,
        selectedOrganization,
        loading,
        error,
        refetchOwnOrganizations: fetchOwnOrganizations,
        changeSelectedOrganization,
      }}
    >
      {children}
    </OrganizationContext.Provider>
  );
} 